// ============================================================
// Sharing helpers: native Web Share (with file when possible),
// WhatsApp, copy link and download. Used by Photo/PDF viewers.
// ============================================================

/** Try the native share sheet with the actual file (mobile). Returns true when it handled the share. */
export async function shareNative({ url, filename, title }) {
  try {
    if (await isNative()) {
      try {
        const files = await fetchAsFiles([{ url, filename }]);
        await nativeShareFiles(files, title || filename);
        return true;
      } catch (e) { /* older build without plugins → web path */ }
    }
    if (!navigator.share) return false;
    // Prefer sharing the real file so WhatsApp & friends attach the media
    try {
      const resp = await fetch(url);
      const blob = await resp.blob();
      const file = new File([blob], filename || "file", { type: blob.type || "application/octet-stream" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: title || filename || "" });
        return true;
      }
    } catch (e) { /* fall back to URL share */ }
    await navigator.share({ title: title || filename || "", url });
    return true;
  } catch (e) {
    // AbortError = user closed the sheet; treat as handled
    return e && e.name === "AbortError";
  }
}

export function shareWhatsApp({ url, text }) {
  const msg = `${text ? `${text}\n` : ""}${url}`;
  window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
}

export async function copyLink(url) {
  try {
    await navigator.clipboard.writeText(url);
    return true;
  } catch (e) {
    try {
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      return true;
    } catch (err) {
      return false;
    }
  }
}

export async function downloadFile({ url, filename }) {
  try {
    const resp = await fetch(url);
    const blob = await resp.blob();
    if (await isNative()) {
      // WKWebView/Android WebView can't download: open the share sheet with the real file ("Save to Files")
      try { return (await nativeShareFiles([new File([blob], filename || "file", { type: blob.type })], filename)) !== "cancelled"; } catch (e) { /* fall through */ }
    }
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename || "download";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
    return true;
  } catch (e) {
    return false;
  }
}

/** "OnSiteComms - 1_Covington - 2026-10-01.jpg" (job number + first word of the customer, when known) */
export function jobTag(job) {
  if (!job || job.job_number == null || job.job_number === "") return "";
  const first = String(job.customer_name || "").trim().split(/\s+/)[0] || "";
  return `${job.job_number}${first ? `_${first}` : ""}`.replace(/[\\/:*?"<>|]/g, "");
}

export function mediaFilename(media, job, fallbackExt = "jpg") {
  const fromName = (media?.filename || "").match(/\.([a-z0-9]{2,4})$/i)?.[1];
  const ext = fromName || (media?.kind === "video" ? "mp4" : media?.kind === "document" ? "pdf" : fallbackExt);
  return `${mediaBaseName(media, job)}.${ext.toLowerCase()}`;
}

/** Same pattern without extension — for native gallery saves (plugin adds the extension) */
export function mediaBaseName(media, job) {
  const d = media?.created_at ? new Date(media.created_at) : new Date();
  const p2 = (n) => String(n).padStart(2, "0");
  const stamp = `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
  const tag = jobTag(media?.job_number != null && media?.job_number !== "" ? media : job);
  return `OnSiteComms - ${tag ? `${tag} - ` : ""}${stamp}`;
}

const EXT_BY_TYPE = { "image/jpeg": "jpg", "image/png": "png", "image/webp": "webp", "image/heic": "heic", "video/mp4": "mp4", "video/quicktime": "mov", "video/webm": "webm", "application/pdf": "pdf" };

async function fetchAsFiles(items) {
  const files = [];
  for (const it of items) {
    const resp = await fetch(it.url);
    if (!resp.ok) throw new Error(`fetch ${resp.status}`);
    const blob = await resp.blob();
    let name = it.filename || "file";
    if (!/\.[a-z0-9]{2,4}$/i.test(name)) name += `.${EXT_BY_TYPE[blob.type] || (blob.type.startsWith("video/") ? "mp4" : "jpg")}`;
    files.push(new File([blob], name, { type: blob.type || "application/octet-stream" }));
  }
  return files;
}

const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(",")[1] || "");
  r.onerror = reject;
  r.readAsDataURL(blob);
});

async function isNative() {
  try { const { Capacitor } = await import("@capacitor/core"); return Capacitor.isNativePlatform(); } catch (e) { return false; }
}

/** Can this device put the real file into a share sheet? (native app or Web Share API with files) */
export async function canShareFiles() {
  return (await isNative()) || !!(typeof navigator !== "undefined" && navigator.canShare);
}

/** Native (iOS/Android): write files to the app cache and open the system share sheet with the REAL files. */
async function nativeShareFiles(files, title) {
  const { Filesystem, Directory } = await import("@capacitor/filesystem");
  const { Share } = await import("@capacitor/share");
  const uris = [];
  const used = new Set();
  for (const f of files) {
    let name = f.name;
    if (used.has(name)) { const m = name.match(/^(.*)(\.[a-z0-9]+)$/i); name = m ? `${m[1]} (${used.size + 1})${m[2]}` : `${name} (${used.size + 1})`; }
    used.add(name);
    const { uri } = await Filesystem.writeFile({ path: `share/${name}`, data: await blobToBase64(f), directory: Directory.Cache, recursive: true });
    uris.push(uri);
  }
  try {
    await Share.share({ files: uris, title: title || "" });
    return "shared";
  } catch (e) {
    if (String(e?.message || e || "").toLowerCase().includes("cancel")) return "cancelled";
    throw e;
  }
}

/**
 * "Save" button: native → straight into the phone gallery (album OnSiteComms) via the Media plugin;
 * if that fails (or on web) → saveFiles (share sheet / download).
 * Returns "saved" | "shared" | "downloaded" | "cancelled" | false
 */
export async function saveToDevice(items) {
  if (await isNative()) {
    try {
      const { saveToGallery } = await import("./gallery");
      const n = await saveToGallery(items.map((it) => ({
        path: it.url,
        kind: it.kind === "video" || /\.(mp4|mov|webm)$/i.test(it.filename || "") ? "video" : "photo",
        fileName: (it.filename || "").replace(/\.[a-z0-9]+$/i, "") || undefined,
      })));
      if (n === items.length) return "saved";
    } catch (e) { /* fall through to the share sheet */ }
  }
  return saveFiles(items);
}

/**
 * Save one or many files to the device. Native/iOS Safari: ONE share sheet with all
 * files (-> "Save N Images" in Photos). Web: sequential downloads.
 * Returns "shared" | "downloaded" | "cancelled" | false
 */
export async function saveFiles(items, title) {
  try {
    const files = await fetchAsFiles(items);
    if (!files.length) return false;
    if (await isNative()) {
      try { return await nativeShareFiles(files, title); } catch (e) { /* older build without plugins → web path */ }
    }
    if (navigator.canShare && navigator.canShare({ files })) {
      await navigator.share({ files, title: title || "" });
      return "shared";
    }
    for (const f of files) {
      const objectUrl = URL.createObjectURL(f);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = f.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
    }
    return "downloaded";
  } catch (e) {
    if (e && e.name === "AbortError") return "cancelled";
    return false;
  }
}

/**
 * Share a plain invite link (personal ?connect= / company ?join=).
 * iOS/Android: native share sheet via @capacitor/share.
 * Web: Web Share API when available; final fallback copies the link.
 *
 * Returns: "shared" | "copied" | "cancelled" | "failed"
 */
export async function shareLink({ title, text, url }) {
  try {
    const { Capacitor } = await import("@capacitor/core");
    if (Capacitor.isNativePlatform()) {
      try {
        const { Share } = await import("@capacitor/share");
        await Share.share({ title, text, url, dialogTitle: title });
        return "shared";
      } catch (e) {
        const msg = String(e?.message || e || "").toLowerCase();
        if (msg.includes("cancel")) return "cancelled";
        // plugin missing in an older native build -> fall through to web fallbacks
      }
    }
  } catch (e) { /* capacitor not available */ }
  if (typeof navigator !== "undefined" && navigator.share) {
    try {
      await navigator.share({ title, text, url });
      return "shared";
    } catch (e) {
      if (e && (e.name === "AbortError" || e.name === "NotAllowedError")) return "cancelled";
      // fall through to clipboard
    }
  }
  const copied = await copyLink(url);
  return copied ? "copied" : "failed";
}

/**
 * Save an image (data URL) to the user's device.
 * iOS/Android: uses the native share sheet (user taps "Save Image" -> photo gallery).
 * Desktop/fallback: downloads the file.
 */
export async function saveImageDataUrl(dataUrl, filename = "photo.jpg") {
  try {
    const resp = await fetch(dataUrl);
    const blob = await resp.blob();
    const file = new File([blob], filename, { type: blob.type || "image/jpeg" });
    if (await isNative()) {
      try {
        const { saveToGallery } = await import("./gallery");
        if ((await saveToGallery([{ path: dataUrl, kind: "photo", fileName: filename.replace(/\.[a-z0-9]+$/i, "") }])) === 1) return "saved";
      } catch (e) { /* fall through */ }
      try { return await nativeShareFiles([file], filename); } catch (e) { /* fall through */ }
    }
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file] });
      return "shared";
    }
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
    return "downloaded";
  } catch (e) {
    if (e && e.name === "AbortError") return "cancelled";
    return false;
  }
}
